
import { AlertCircle, RefreshCw } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface ContractExpiryWarningProps {
  daysUntilExpiry: number;
  hasRenewalInfo: boolean;
}

export function ContractExpiryWarning({ daysUntilExpiry, hasRenewalInfo }: ContractExpiryWarningProps) {
  const { t } = useTranslation();
  const isExpiringSoon = daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
  const isExpired = daysUntilExpiry < 0;

  // If contract has renewal info and is expired, we don't need to show a warning
  if (hasRenewalInfo && isExpired) {
    return (
      <div className="p-4 rounded-md bg-blue-500/15 flex items-center gap-3 animate-fade-in">
        <RefreshCw className="h-5 w-5 text-blue-500" />
        <div>
          <p className="font-medium">{t('contractAutoRenewed', 'This contract has auto-renewed')}</p>
          <p className="text-sm text-muted-foreground">
            {t('contractAutoRenewedDetails', 'The contract follows the renewal terms specified in its contract type')}
          </p>
        </div>
      </div>
    );
  }

  // For auto-renewing contracts that are about to expire, show a different message
  if (hasRenewalInfo && isExpiringSoon) {
    return (
      <div className="p-4 rounded-md bg-blue-500/15 flex items-center gap-3 animate-fade-in">
        <RefreshCw className="h-5 w-5 text-blue-500" />
        <div>
          <p className="font-medium">
            {t('contractWillRenew', `This contract will auto-renew in ${daysUntilExpiry} days`, { days: daysUntilExpiry })}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('contractWillFollowTerms', 'The contract will follow the renewal terms specified in its contract type')}
          </p>
        </div>
      </div>
    );
  }

  // For regular contracts
  if (!hasRenewalInfo && (isExpiringSoon || isExpired)) {
    return (
      <div className={`p-4 rounded-md ${isExpired ? 'bg-destructive/15' : 'bg-warning/15'} flex items-center gap-3 animate-fade-in`}>
        <AlertCircle className={`h-5 w-5 ${isExpired ? 'text-destructive' : 'text-warning'}`} />
        <div>
          <p className="font-medium">
            {isExpired 
              ? t('contractExpired', `Contract expired ${Math.abs(daysUntilExpiry)} days ago`, { days: Math.abs(daysUntilExpiry) })
              : t('contractExpiring', `Contract expiring in ${daysUntilExpiry} days`, { days: daysUntilExpiry })
            }
          </p>
          <p className="text-sm text-muted-foreground">
            {isExpired 
              ? t('considerRenewing', 'Consider renewing or replacing this contract')
              : t('takeAction', 'Take action before the contract expires')
            }
          </p>
        </div>
      </div>
    );
  }

  return null;
}
